<template>
  <div class="input-group flex-nowrap contest-summary-view" :style="stylePosition">
    <span
        v-if="position && !isHandRaise"
        class="input-group-text bg-white p-0"
        style="display: grid; grid-template-areas: 'pos up' 'pos down'; grid-template-columns: 3rem auto;"
    >
      <input
          type="number"
          class="contest-position-input"
          style="grid-area: pos; place-self: stretch; text-align: center"
          :value="position"
          :disabled="locked"
          min="1"
          :max="selectedQty"
          @change="changePosition"
          @keydown.enter.prevent="changePosition"
      >
      <button
        type="button"
        class="btn btn-light btn-sm rounded-0"
        :disabled="isAtTop || locked"
        :aria-label="$t('backend.actions.move_up')"
        @click="moveUp"
      >
        <AVIcon icon="chevron-up" />
      </button>
      <button
        type="button"
        class="btn btn-light btn-sm rounded-0"
        :disabled="isAtBottom || locked"
        :aria-label="$t('backend.actions.move_down')"
        @click="moveDown"
      >
        <AVIcon icon="chevron-down" />
      </button>
    </span>
    <button
        type="button"
        style="min-width: 0"
        class="btn flex-grow-1 hstack justify-content-between"
        :class="{
          'btn-primary': isSelected,
          'btn-secondary': !isSelected,
          disabled: (disabled && !isSelected) || locked
        }"
        @click="handleContest"
      >
      <span class="text-truncate"
            :class="{'text-light': isSelected}"
            v-text="contest.label"
      ></span>
      <span class="d-flex flex-column align-items-center flex-shrink-1" style="min-width: 0">
        <span class="badge" :class="{'text-bg-light': isSelected, 'text-bg-primary': !isSelected}">
          {{ $t("js.backend.voting_round_modal.option_counter", { n: contest.options_count, plural: contest.options_count }) }}
        </span>
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { PropType, Locale } from "@/types";
import { ContestSelectionListContest } from "@/components/backend/ContestSelectionList.vue";

const props = defineProps({
  contest: {
    type: Object as PropType<ContestSelectionListContest>,
    required: true,
  },
  currentLocale: {
    type: String as PropType<Locale>,
    required: true,
  },
  isSelected: {
    type: Boolean,
    required: true,
  },
  editMode: {
    type: Boolean,
    required: true,
  },
  position: {
    type: Number,
    required: true,
  },
  selectedQty: {
    type: Number,
    required: true,
  },
  orderError: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isHandRaise: {
    type: Boolean,
    default: false,
  },
  locked: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(["handleContest", "handleContestPosition"]);

const maxOrder = ref<number>(0);

const stylePosition = computed(() => isNaN(props.position)
  ? { "grid-column": props.isSelected ? 2 : 1 }
  : { "grid-row": props.position, "grid-column": props.isSelected ? 2 : 1 }
);

const isAtTop = computed(() => props.position === 1);

const isAtBottom = computed(() => props.position === props.selectedQty);

const handleContest = () => {
  if (!props.isSelected && props.disabled) return;
  if (!props.editMode) emits("handleContest", props.contest.reference, props.position);
};

const moveUp = () => {
  if (isAtTop.value || props.editMode) return;
  emits("handleContestPosition", props.contest.reference, props.position - 1);
};

const moveDown = () => {
  if (isAtBottom.value || props.editMode) return;
  emits("handleContestPosition", props.contest.reference, props.position + 1);
};

const changePosition = (event: Event) => {
  if (props.editMode) return;

  (event.target as HTMLInputElement).value = Math.min(Math.max(1, Number((event.target as HTMLInputElement).value)), props.selectedQty).toString();
  emits("handleContestPosition", props.contest.reference, (event.target as HTMLInputElement).value);
};

watch(() => props.selectedQty, () => maxOrder.value = props.selectedQty);
</script>

<style lang="css" scoped>
.contest-position-input {
  border: none;
  background: none;
  -moz-appearance: textfield;
}

.contest-position-input::-webkit-inner-spin-button,
.contest-position-input::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.contest-summary-view {
  opacity: 1.0;
  min-width: 100%;
}

.disabled {
  opacity: 0.7;
}
</style>
